import React, { memo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Icon } from '@gerdau/hefesto';
import { Tag } from '@gerdau/hefesto-mais-web';

import {
  NoImageThumbnail,
  CardContent,
  ProductInfo,
  ProductPrice,
  AdditionalInfo,
  CardContainer,
  CodeInfo,
  ButtonsContainer,
  AditionalInfoContainer,
  PriceAgreementContainer,
  InfoContainer,
  WishlistButton
} from './styles';
import favoriteIconEmpty from '../../../assets/images/favorite_empty.svg';
import favoriteIconFill from '../../../assets/images/favorite_fill.svg';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { addToast } from '../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../store/ducks/toast/types';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import { useLocation } from 'react-router-dom';
import { getTaggingInformationScreen } from '../../../utils/Tagging';
import {
  CartItemPayload,
  SortProductsItem
} from '../../../services/MVP/models';
import { useCart } from '../../../pages/Purchase/LongSteel/ShoppingCartMVP/useCart';
import { useCartService } from '../../../services/MVP/useCartService';
import LoadingModal from '../../LoadingModal';
import { EOrderSettingsRoutes } from '../../../pages/Purchase/LongSteel/OrderSettingsMvp/orderSettings.routes';
import { currencyFormatter } from '../../../pages/Purchase/LongSteel/CheckoutMVP/utils/formatterHelper';
import { updateCartItemsQuantity } from '../../../store/ducks/cart-items-mvp/actions';
import { ApplicationState } from '../../../store';
import { usePricingService } from '../../../services/MVP/usePricingService';
import { GetFlag } from '../../../utils/featureFlag';
import {
  setTipoCompraForItemId,
  setLastTipoCompra
} from '../../../services/MVP/tipoCompraMap';
import { useFavoriteToggle } from '../../../hooks/useFavoriteToggle';
import type { FavoriteTaggingData } from '../../../hooks/useFavoriteToggle';

export interface ProductImageProps {
  desktopSource: string;
  mobileSource?: string;
  breakpoint?: number;
}

export const ProductImage = styled.div<ProductImageProps>`
  background: url(${(props) => `${props.desktopSource}?width=128`});
  min-height: 8rem;
  max-height: 8rem;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  @media (max-width: ${({ breakpoint }) => breakpoint ?? '992px'}) {
    background-image: url(${(props) => props.mobileSource});
  }
`;

interface ProductCardProps {
  agreedPrice?: boolean;
  image?: ProductImageProps;
  product?: SortProductsItem;
  showLink?: boolean;
  typeCode?: 'gerdauCode' | 'internalCode';
  onRemoveFavorite?: (sku: string) => void;
  taggingContext?: { screenName: string; tipoCompra: string };
}

const AgreementChip = ({ agreedPrice }: { agreedPrice?: boolean }) => {
  if (!agreedPrice) {
    return null;
  }

  return (
    <PriceAgreementContainer>
      <Tag variant="success">Preço acordado</Tag>
    </PriceAgreementContainer>
  );
};

const WithoutPriceChip = ({ withoutPrice }: { withoutPrice?: boolean }) => {
  if (!withoutPrice) {
    return null;
  }

  return (
    <PriceAgreementContainer>
      <Tag variant="danger">Produto indisponível</Tag>
    </PriceAgreementContainer>
  );
};

const ImageProduct = ({ image }: { image?: ProductImageProps }) => {
  if (!image) {
    return (
      <NoImageThumbnail>Imagem indisponível para este produto</NoImageThumbnail>
    );
  }

  return <ProductImage {...image} />;
};

const ProductDetails = ({
  showLink,
  onClick
}: {
  showLink: boolean;
  onClick: () => void;
}) => {
  if (!showLink) {
    return null;
  }

  return (
    <Button
      name="btn-show-details"
      variant="tertiary"
      onClick={onClick}
      children={'Mostrar detalhes'}
    />
  );
};

const ProductCard = ({
  agreedPrice,
  product,
  image,
  showLink = true,
  typeCode,
  onRemoveFavorite,
  taggingContext
}: ProductCardProps) => {
  if (!product) {
    return null;
  }

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );

  const { addItemToCart } = useCart();
  const { createNewCart } = useCartService();
  const { getCartId } = useCartService();
  const { push } = useHistory();
  const { getPrice } = usePricingService();

  const [loading, setLoading] = React.useState(false);

  const dispatch = useDispatch();
  const descricao_gpm =
    product.custom_attributes.find(
      (att) => att.attribute_code === 'descricao_gpm'
    )?.value ?? '';

  const { pathname } = useLocation();
  const { commerceScreenName } = getTaggingInformationScreen(pathname);

  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');
  const isFavoriteEnabled = GetFlag('favorite_product');

  const price = getPrice(product);

  const favoriteTaggingData: FavoriteTaggingData = {
    name: product.name,
    category: product.type_id,
    price: price?.creditPrice,
    quantity: '1',
    um: price?.um,
    isAgreementPrice: (price?.isAgreementPrice?.length ?? 0) > 0
  };
  const { isFavorited, handleToggleFavorite } = useFavoriteToggle(
    product?.extension_attributes?.added_to_wishlist ?? false,
    product.sku,
    favoriteTaggingData,
    taggingContext?.screenName ?? commerceScreenName ?? 'vitrine'
  );

  // Buscar aqui key do carrinho ativo
  const handleAddProduct = async () => {
    if (!price || Number(price.creditPrice) <= 0) {
      return null;
    }
    try {
      setLoading(true);
      const existingCart = getCartId(shoppingCartState.emitterId);
      let cart;
      let currentQuantity;

      if (!existingCart || existingCart.length === 0) {
        const newCart = await createNewCart(
          shoppingCartState.emitterId,
          shoppingCartState.receiverId,
          true
        );
        cart = newCart?.cartId;
        currentQuantity = 1;
      } else {
        cart = existingCart;
        currentQuantity = cartQuantity + 1;
      }

      if (!cart || cart.length === 0) {
        throw new Error();
      }

      if (currentQuantity > 30) {
        return dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Seu carrinho atingiu o limite de 30 produtos.',
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
      }

      const formattedProduct = {
        sku: product.sku,
        qty: 1,
        quote_id: cart,
        extension_attributes: {
          measure_unity_gerdau: price.um,
          destination_gerdau: price.destination,
          payment_type_gerdau: price.paymentTerms,
          has_agreed_price_gerdau: price.isAgreementPrice.length !== 0,
          ...(price.salesAgreementData.expirationDate && {
            price_agreement_date_gerdau: price.salesAgreementData.expirationDate
          }),
          sap_fields: {
            o_header_sap: [],
            o_item_sap: [],
            o_price_sap: []
          }
        }
      } as CartItemPayload;

      const addItem = await addItemToCart(formattedProduct, currentQuantity, {
        withOptions: false,
        withPrice: false
      });

      if (addItem.item_id) {
        const tagScreenName =
          taggingContext?.screenName ?? commerceScreenName ?? 'vitrine';
        const tagTipoCompra = taggingContext?.tipoCompra ?? 'vitrine';
        dispatch(
          sendTagging({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'BRL',
              value: `${price.creditPrice}`,
              screenName: tagScreenName,
              tipo_compra: tagTipoCompra,
              paginacao: '1',
              items: [
                {
                  screenName: tagScreenName,
                  price: `${price.creditPrice}`,
                  unidade_de_medida: `${price.um}`,
                  item_id: `${product.sku}`,
                  item_name: `${product.name}`,
                  tipo_compra: tagTipoCompra,
                  item_category: `${product.type_id}`,
                  cod_gerdau: `${product.sku}`,
                  preco_acordado: price.isAgreementPrice.length > 0,
                  quantity: '1',
                  favoritado: isFavorited
                }
              ]
            }
          })
        );

        setTipoCompraForItemId(addItem.item_id, tagTipoCompra);
        setLastTipoCompra(tagTipoCompra);

        dispatch(updateCartItemsQuantity(currentQuantity ?? 0));

        dispatch(
          addToast({
            message: `Produto adicionado ao carrinho. (SKU ${product.sku})`,
            type: EToastTypes.SUCCESS,
            durationInMS: 5000,
            spaceToTop: 77
          })
        );
        return;
      }

      throw new Error();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Navegação para a destalhes do produto
  const handleShowDetails = () => {
    dispatch(
      sendTagging({
        event: 'select_item',
        ecommerce: {
          currency: 'BRL',
          value: `${price}`,
          screenName: 'vitrine',
          tipo_compra: 'vitrine',
          paginacao: '1',
          items: [
            {
              screenName: 'vitrine',
              price: `${price.creditPrice}`,
              unidade_de_medida: `${price.um}`,
              item_id: `${product.sku}`,
              item_name: `${product.name}`,
              tipo_compra: `vitrine`,
              item_category: `${descricao_gpm}`,
              cod_gerdau: `${product.sku}`,
              preco_acordado: price.isAgreementPrice.length > 0,
              quantity: price.quantity ?? '1'
            }
          ]
        }
      })
    );
    push(
      `${EOrderSettingsRoutes.CATALOG}/${EOrderSettingsRoutes.PRODUCT}/${product.sku}`
    );
  };

  const hasFilterInternalCode = newSearchItemsEndpointFlag
    ? Boolean(product?.extension_attributes?.internal_code) &&
      typeCode === 'internalCode'
    : Boolean(product?.price?.customerProduct) && typeCode === 'internalCode';

  const productCode =
    typeCode === 'internalCode'
      ? newSearchItemsEndpointFlag
        ? product?.extension_attributes?.internal_code || product.sku
        : product?.price?.customerProduct || product.sku
      : product.sku;

  const productWithoutPrice =
    price?.creditPrice === '' ||
    !price?.creditPrice ||
    Number(price?.creditPrice) <= 0;

  return (
    <CardContainer className="product-cards">
      <LoadingModal
        enabled={loading}
        title="Aguarde alguns instantes"
        message="Carregando os dados..."
      />
      <AgreementChip agreedPrice={agreedPrice} />
      <WithoutPriceChip withoutPrice={productWithoutPrice} />
      {onRemoveFavorite ? (
        <WishlistButton
          onClick={() => onRemoveFavorite(product.sku)}
          aria-label="Remover dos favoritos"
        >
          <Icon option="Trash" variant="danger" size="xs" />
        </WishlistButton>
      ) : (
        isFavoriteEnabled && (
          <WishlistButton
            onClick={handleToggleFavorite}
            aria-label={
              isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'
            }
          >
            <img
              src={isFavorited ? favoriteIconFill : favoriteIconEmpty}
              alt=""
            />
          </WishlistButton>
        )
      )}
      <ImageProduct image={image} />

      <CardContent>
        <CodeInfo>
          Cód. {hasFilterInternalCode ? 'Interno' : 'Gerdau'}: {productCode}
        </CodeInfo>
        <ProductInfo $title>{descricao_gpm}</ProductInfo>
        <ProductInfo>{product.name}</ProductInfo>
        <InfoContainer>
          {!productWithoutPrice && (
            <>
              <ProductPrice>
                {currencyFormatter({
                  amount: agreedPrice
                    ? Number(price.salesAgreementData.salesAgreement)
                    : Number(price.creditPrice),
                  currency: 'BRL'
                })}
                <span style={{ fontSize: '0.88rem', fontWeight: '400' }}>
                  /{price.um}
                </span>
              </ProductPrice>
              <AditionalInfoContainer>
                <AdditionalInfo
                  title={`Preço/${price.um}: ${currencyFormatter({
                    amount: agreedPrice
                      ? Number(price.salesAgreementData.salesAgreement)
                      : Number(price.creditPrice),
                    currency: 'BRL'
                  })}`}
                >
                  Preço/
                  {agreedPrice
                    ? price.salesAgreementData.agreementUm
                    : price.um}
                  :{' '}
                  {currencyFormatter({
                    amount: agreedPrice
                      ? Number(price.salesAgreementData.salesAgreement)
                      : Number(price.creditPrice),
                    currency: 'BRL'
                  })}
                </AdditionalInfo>
                {!newSearchItemsEndpointFlag && (
                  <AdditionalInfo
                    title={`Pagamento: ${
                      agreedPrice
                        ? price.salesAgreementData.paymentTermsDescription
                        : price.paymentTermsDescription
                    }`}
                  >
                    Pagamento:{' '}
                    {agreedPrice
                      ? price.salesAgreementData.paymentTermsDescription
                      : price.paymentTermsDescription}
                  </AdditionalInfo>
                )}
                <AdditionalInfo
                  title={`Destinação: ${
                    agreedPrice
                      ? price.salesAgreementData.destination
                      : price.destination
                  }`}
                >
                  Destinação:{' '}
                  {agreedPrice
                    ? price.salesAgreementData.destinationDescription
                    : price.destinationDescription}
                </AdditionalInfo>
              </AditionalInfoContainer>
            </>
          )}
          <ButtonsContainer>
            <Button
              onClick={handleAddProduct}
              name="button-card"
              variant="secondary"
              disabled={productWithoutPrice}
            >
              {productWithoutPrice
                ? 'Produto indisponível'
                : 'Adicionar ao carrinho'}
            </Button>
            <ProductDetails onClick={handleShowDetails} showLink={showLink} />
          </ButtonsContainer>
        </InfoContainer>
      </CardContent>
    </CardContainer>
  );
};

export default memo(ProductCard);
